import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AgpmRepository } from '../../../store/agpm-state/agpm-state.repository';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, FileRepresentation, SelectOption } from '../../../components/form-builder/form-builder.types';
import { AgpmService } from '../agpm.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { Agpm, AgpmDto, TasksStatus } from '@/types';
import { CurrentTasksService } from '../../current-tasks/current-tasks.service';
import { CurrentTasksRepository } from '../../../store/current-tasks-state/current-tasks-state.repository';
import { TextareaModule } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { AgpmWorkflowBlocksService } from '../agpm-workflow-blocks.service';
import { BlockBuilderComponent } from '../../../components/block-builder/block-builder.component';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { AuthorizationService } from '@/services/authorization.service';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { getAgpmInspectFormObject } from './utils/agpm-inspect.form-object';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';

@Component({
  selector: 'sm-agpm-inspect-notice',
  imports: [
    PageHeaderComponent,
    CommonModule,
    FormBuilderComponent,
    ButtonModule,
    TextareaModule,
    FormsModule,
    TranslateModule,
    BlockBuilderComponent,
    StatusComponent,
    DialogComponent,
    SpinnerComponent,
  ],
  templateUrl: './inspect-notice.component.html',
  styleUrl: './inspect-notice.component.scss',
})
export class AgpmInspectNoticeComponent implements OnInit, OnDestroy {
  agpmRepository = inject(AgpmRepository);
  agpmService = inject(AgpmService);
  messageService = inject(MessageService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  currentTasksService = inject(CurrentTasksService);
  currentTasksRepository = inject(CurrentTasksRepository);
  agpmWorkflowBlocksService = inject(AgpmWorkflowBlocksService);
  authService = inject(AuthorizationService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  translate = inject(TranslateService);
  fileService = inject(FileService);

  breadcrumb = [
    {
      label: 'agpm.breadcrumb.agpm',
      routerLink: ['/agpm-current-notice'],
    },
    {
      label: 'agpm.breadcrumb.agpmConsult',
      routerLink: ['agpm-edit-notice'],
    },
  ];

  acOptions: SelectOption[] = [];

  isPassForValidationDisabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  isDisabled = of(false);
  formObject: DynamicForm = getAgpmInspectFormObject({
    store: this.agpmRepository,
    acOptions: this.acOptions,
    download: (file: FileRepresentation) => this.fileService.download(file, NAMESPACE_ENUM.AGPM),
    openFile: (file: FileRepresentation) => this.fileService.openFile(file, NAMESPACE_ENUM.AGPM),
  });

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  agpmDialogObject?: DialogObject;

  status!: string;

  controlBlocks: DynamicBlocks[] = [];

  comment = new BehaviorSubject<string>('');
  commentError = new BehaviorSubject<string | null>(null);

  loading$ = new BehaviorSubject<boolean>(true);

  isSourceTask = false;

  id: number = this.activatedRoute.snapshot.params['id'];
  version?: number = this.activatedRoute.snapshot.params['version'];

  agpm?: Agpm;

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.agpmRepository.reset();
    this.currentTasksRepository.reset();
  }

  onCommentChange(value: string) {
    this.comment.next(value);
    if (this.commentError.getValue()) {
      this.commentError.next(null);
    }
  }

  loadData() {
    this.loading$.next(true);

    const isVersioned = this.version !== undefined;
    const details$ = isVersioned ? this.agpmService.getAgpmVersion(this.id, this.version!) : this.agpmService.getOneAgpm(this.id);

    forkJoin([details$, this.contractingAuthorityService.getCurrentUserContractingAuthority()]).subscribe(
      ([agpm, acs]) => {
        this.agpm = agpm;
        this.agpmRepository.setNested('response', {
          ...agpm,
          status: agpm.status,
          docs: agpm.docs,
        });
        this.agpmRepository.setNested('agpmServerObject', { ...agpm, acId: agpm.ac.id } as AgpmDto);

        this.acOptions = acs.map(ac => ({ name: ac.nom, code: ac.id }));

        this.formObject = getAgpmInspectFormObject({
          store: this.agpmRepository,
          acOptions: this.acOptions,
          download: f => this.fileService.download(f, NAMESPACE_ENUM.AGPM),
          openFile: f => this.fileService.openFile(f, NAMESPACE_ENUM.AGPM),
          acDisabled: of(agpm.status !== TasksStatus.BROUILLON),
        });

        this.agpmRepository.setNested('dto', { ...agpm, acId: agpm.ac.id } as AgpmDto);
        this.agpmRepository.set('acsSelected', { name: agpm.ac.nom, code: agpm.ac.id });

        this.status = agpm.status;
        this.controlBlocks = this.agpmWorkflowBlocksService.getInspectAgpmBlocks({
          refreshCount: agpm.nbrActualisation,
          status: this.status,
          refresh: () => this.refresh(),
          accessHistory: () => this.router.navigate(['agpm-version-history', this.id]),
          downloadSigned: this.agpmRepository.getNested('response', 'modelAlfrescoId') ? () => this.downloadModel() : undefined,
        });

        this.loading$.next(false);
      },
      err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.loading'),
        });
        this.loading$.next(false);
      }
    );
  }

  downloadModel() {
    this.fileService.download(
      {
        filename: this.agpmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.agpmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.AGPM_MODEL
    );
  }

  refresh() {
    this.loading$.next(true);

    this.agpmService.refreshAgpm(this.agpmRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('agpm.message.success.refreshed'),
        });
        this.router.navigate(['/agpm-edit-notice', this.agpmRepository.getNested('response', 'id')]);
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('agpm.message.error.refreshed'),
        });
        this.loading$.next(false);
      },
    });
  }
}
